import axios, { AxiosInstance } from 'axios';
import { env } from '@/utils/constants/env';
import useAuthState from '@/states/auth.state';
import useTenantState from '@/states/tenant.state';
import Swal from 'sweetalert2';

declare module 'axios' {
  export interface AxiosDefaults {
    cloudStorage?: string;
  }
}

export const axiosTasklist: AxiosInstance = axios.create({
  baseURL: env.apiTasklistBaseUrl,
});

axiosTasklist.defaults.cloudStorage = env.cloudStorage;

axiosTasklist.interceptors.request.use(async config => {
  const token = await useAuthState.getState().getValidToken();
  const activeTenant = useTenantState.getState().currentTenant;

  if (token) config.headers.Authorization = `Bearer ${token}`;
  if (activeTenant) config.headers['X-ACTIVE-TENANT'] = activeTenant.slug;

  return config;
});

// Response interceptor - handle 401 with keycloak-js refresh
axiosTasklist.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    // Handle Trial Expired
    const responseData = error.response?.data;
    if (
      error.response?.status === 403 &&
      (responseData?.error === 'TRIAL_EXPIRED_BLOCKED' || responseData?.message === 'TRIAL_EXPIRED_BLOCKED')
    ) {
      const userProfile = useAuthState.getState().userProfile;
      const trialEndsAt = userProfile?.trial_ends_at;
      const formattedDate = trialEndsAt ? new Date(trialEndsAt).toISOString().split('T')[0] : '';

      Swal.fire({
        icon: 'error',
        title: 'Access Restricted',
        html: `
          <div class="flex flex-col gap-2">
            <p>Your trial has ended on <b>${formattedDate}</b>.</p>
            <p>Please contact support to continue using these features.</p>
            <div class="mt-4">
               <a href="${env.contactUrl}" target="_blank" class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 no-underline inline-block">
                 Contact Support
               </a>
            </div>
          </div>
        `,
        showConfirmButton: false,
        allowOutsideClick: false,
        showCloseButton: true,
      });

      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const newToken = await useAuthState.getState().getValidToken();
        if (newToken) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return axiosTasklist(originalRequest);
        } else {
          globalThis.location.href = '/login';
          localStorage.removeItem('auth-storage');
          throw new Error('Authentication required');
        }
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        globalThis.location.href = '/login';
        localStorage.removeItem('auth-storage');
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);
